import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useServices } from "@/hooks/useServices";
import { getSeatFinderAvailability } from "@/lib/seatFinderAvailability";
import { ServiceContainerContext, defaultServiceContainer } from "@/services/serviceContainer";
import NotFound from "./pages/NotFound.tsx";
import Privacy from "./pages/Privacy.tsx";
import Terms from "./pages/Terms.tsx";
import { CookieBanner } from "./components/shared/CookieBanner.tsx";
import { initConsent } from "./lib/consent.ts";
import i18n from "./i18n.ts";
import type { SupportedLocale } from "./services/localeResolver.ts";

const queryClient = new QueryClient();

initConsent();
const DemoGallery = lazy(() => import("./pages/DemoGallery.tsx"));
const Landing = lazy(() => import("./pages/Landing.tsx"));
const BlogIndex = lazy(() => import("./pages/BlogIndex.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const ForBusiness = lazy(() => import("./pages/ForBusiness.tsx"));
const BuildEvent = lazy(() => import("./pages/BuildEvent.tsx"));
const Login = lazy(() => import("./pages/Login.tsx"));
const ResetCredential = lazy(() => import("./pages/ResetCredential.tsx"));
const SupabaseRecoveryPassword = lazy(() => import("./pages/SupabaseRecoveryPassword.tsx"));
const GoogleDriveCallback = lazy(() => import("./pages/GoogleDriveCallback.tsx"));
const EventAdmin = lazy(() => import("./pages/EventAdmin.tsx"));
const OverviewSectionRoute = lazy(() => import("./components/event-admin/sections/OverviewSectionRoute.tsx"));
const PhotosSectionRoute = lazy(() => import("./components/event-admin/sections/PhotosSectionRoute.tsx"));
const VoiceSectionRoute = lazy(() => import("./components/event-admin/sections/VoiceSectionRoute.tsx"));
const StickerSectionRoute = lazy(() => import("./components/event-admin/sections/StickerSectionRoute.tsx"));
const SettingsSectionRoute = lazy(() => import("./components/event-admin/sections/SettingsSectionRoute.tsx"));
const HuntSectionRoute = lazy(() => import("./components/event-admin/sections/HuntSectionRoute.tsx"));
const EventUpsell = lazy(() => import("./pages/EventUpsell.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const EventGallery = lazy(() => import("./pages/EventGallery.tsx"));
const SharedPhoto = lazy(() => import("./pages/SharedPhoto.tsx"));
const RsvpPage = lazy(() => import("./pages/RsvpPage.tsx"));
const SEAT_FINDER_AVAILABILITY_RETRY_ATTEMPTS = 2;
const SEAT_FINDER_AVAILABILITY_RETRY_DELAY_MS = 350;

const waitFor = (ms: number) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

const withRetry = async <T,>(action: () => Promise<T>, attempts: number, delayMs: number): Promise<T> => {
  let attempt = 0;
  let lastError: unknown;

  while (attempt < attempts) {
    try {
      return await action();
    } catch (error) {
      lastError = error;
      attempt += 1;

      if (attempt >= attempts) {
        break;
      }

      await waitFor(delayMs);
    }
  }

  throw lastError;
};

const SeatFinderRouteError = () => {
  const { t } = useTranslation();

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted px-6 py-10">
      <section className="w-full max-w-md rounded-2xl border border-border bg-background p-6 text-center shadow-sm">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("public.seatFinder.unavailable.title", { defaultValue: "Seat Finder is temporarily unavailable" })}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {t("public.seatFinder.unavailable.description", {
            defaultValue: "We couldn't verify seat finder availability right now. Refresh the page and try again.",
          })}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-5 inline-flex h-10 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground"
        >
          {t("public.seatFinder.unavailable.retry", { defaultValue: "Try again" })}
        </button>
      </section>
    </main>
  );
};

export const LocalizedLandingRoute = ({ locale }: { locale: SupportedLocale }) => {
  const [isReady, setIsReady] = useState(i18n.resolvedLanguage === locale || i18n.language === locale);

  useEffect(() => {
    let active = true;

    const syncLocale = async () => {
      if (i18n.resolvedLanguage !== locale && i18n.language !== locale) {
        await i18n.changeLanguage(locale);
      }
      if (active) {
        setIsReady(true);
      }
    };

    setIsReady(i18n.resolvedLanguage === locale || i18n.language === locale);
    void syncLocale();

    return () => {
      active = false;
    };
  }, [locale]);

  if (!isReady) {
    return null;
  }

  return <LazyRoute><Landing /></LazyRoute>;
};

const LocalizedPageRoute = ({ locale, children }: { locale: SupportedLocale; children: ReactNode }) => {
  const [isReady, setIsReady] = useState(i18n.resolvedLanguage === locale || i18n.language === locale);

  useEffect(() => {
    let active = true;

    const syncLocale = async () => {
      if (i18n.resolvedLanguage !== locale && i18n.language !== locale) {
        await i18n.changeLanguage(locale);
      }
      if (active) setIsReady(true);
    };

    setIsReady(i18n.resolvedLanguage === locale || i18n.language === locale);
    void syncLocale();

    return () => { active = false; };
  }, [locale]);

  if (!isReady) return null;
  return <>{children}</>;
};

const getSupabaseRecoveryRedirect = () => {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const search = new URLSearchParams(window.location.search);
  const type = hash.get("type") || search.get("type");
  const hasRecoveryTokens = type === "recovery" || hash.has("access_token") || search.has("code");

  if (!hasRecoveryTokens) return null;
  return `/auth/recovery${window.location.search}${window.location.hash}`;
};

const LandingRedirect = () => <Navigate to={getSupabaseRecoveryRedirect() || "/hr"} replace />;

const App = () => (
  <ServiceContainerContext.Provider value={defaultServiceContainer}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="thenea-theme">
          <Sonner />
          <BrowserRouter>
              <Routes>
                <Route path="/" element={<LandingRedirect />} />
                <Route path="/hr" element={<LocalizedLandingRoute locale="hr" />} />
                <Route path="/en" element={<LocalizedLandingRoute locale="en" />} />
                <Route path="/hr/blog" element={<LocalizedPageRoute locale="hr"><LazyRoute><BlogIndex locale="hr" /></LazyRoute></LocalizedPageRoute>} />
                <Route path="/en/blog" element={<LocalizedPageRoute locale="en"><LazyRoute><BlogIndex locale="en" /></LazyRoute></LocalizedPageRoute>} />
                <Route path="/hr/blog/:slug" element={<LocalizedPageRoute locale="hr"><LazyRoute><BlogPost locale="hr" /></LazyRoute></LocalizedPageRoute>} />
                <Route path="/en/blog/:slug" element={<LocalizedPageRoute locale="en"><LazyRoute><BlogPost locale="en" /></LazyRoute></LocalizedPageRoute>} />
                <Route path="/hr/za-poslovne" element={<LocalizedPageRoute locale="hr"><LazyRoute><ForBusiness locale="hr" /></LazyRoute></LocalizedPageRoute>} />
                <Route path="/en/for-business" element={<LocalizedPageRoute locale="en"><LazyRoute><ForBusiness locale="en" /></LazyRoute></LocalizedPageRoute>} />
                <Route path="/build" element={<LazyRoute><BuildEvent /></LazyRoute>} />
                <Route path="/login" element={<LazyRoute><Login /></LazyRoute>} />
                <Route path="/reset" element={<LazyRoute><ResetCredential /></LazyRoute>} />
                <Route path="/auth/recovery" element={<LazyRoute><SupabaseRecoveryPassword /></LazyRoute>} />
                <Route path="/auth/google-drive/callback" element={<LazyRoute><GoogleDriveCallback /></LazyRoute>} />
                <Route path="/demo" element={<LazyRoute><DemoGallery /></LazyRoute>} />
                <Route path="/privatnost" element={<Privacy />} />
                <Route path="/uvjeti" element={<Terms />} />
                <Route path="/hr/privatnost" element={<LocalizedPageRoute locale="hr"><Privacy /></LocalizedPageRoute>} />
                <Route path="/hr/uvjeti" element={<LocalizedPageRoute locale="hr"><Terms /></LocalizedPageRoute>} />
                <Route path="/en/privacy" element={<LocalizedPageRoute locale="en"><Privacy /></LocalizedPageRoute>} />
                <Route path="/en/terms" element={<LocalizedPageRoute locale="en"><Terms /></LocalizedPageRoute>} />
                <Route path="/e/:slug" element={<LazyRoute><EventGallery /></LazyRoute>} />
                <Route path="/e/:slug/photo/:photoId" element={<LazyRoute><SharedPhoto /></LazyRoute>} />
                <Route path="/e/:slug/admin" element={<LazyRoute><EventAdmin /></LazyRoute>}>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<LazyRoute><OverviewSectionRoute /></LazyRoute>} />
                  <Route path="photos" element={<LazyRoute><PhotosSectionRoute /></LazyRoute>} />
                  <Route path="voice" element={<LazyRoute><VoiceSectionRoute /></LazyRoute>} />
                  <Route path="sticker" element={<LazyRoute><StickerSectionRoute /></LazyRoute>} />
                  <Route path="settings" element={<LazyRoute><SettingsSectionRoute /></LazyRoute>} />
                  <Route path="hunt" element={<LazyRoute><HuntSectionRoute /></LazyRoute>} />
                </Route>
                <Route path="/e/:slug/add-ons" element={<LazyRoute><EventUpsell /></LazyRoute>} />
                <Route path="/e/:slug/rsvp" element={<LazyRoute><RsvpPage /></LazyRoute>} />
                <Route path="/e/:slug/rsvp/:token" element={<LazyRoute><RsvpPage /></LazyRoute>} />
                <Route path="/admin" element={<LazyRoute><Admin /></LazyRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <CookieBanner />
            </BrowserRouter>
        </ThemeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ServiceContainerContext.Provider>
);

const LazyRoute = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={null}>
    {children}
  </Suspense>
);

export default App;
